import React, { useEffect, useMemo, useState } from 'react';
import { Container } from 'react-bootstrap';
import { useHistory, useParams } from 'react-router-dom';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import {
  FiArrowLeft,
  FiAward,
  FiClipboard,
  FiHelpCircle,
  FiUsers,
} from 'react-icons/fi';

import BasePage from '../../../components/BasePage';
import ChartTooltip from '../../Dashboard/ChartTooltip';
import { PALETTE } from '../../Dashboard/chartTheme';

import api from '../../../services/api';

import {
  BackLink,
  Card,
  CardHeader,
  DonutCenter,
  DonutLayout,
  EmptyState,
  FadeIn,
  HighlightRow,
  InsightPill,
  KpiCell,
  KpiRow,
  LegendItem,
  LegendList,
  QuestionGrid,
  QuestionNumber,
  ResultsContainer,
  Skeleton,
  SkeletonCircle,
  SkeletonRowSpread,
  SkeletonStack,
  TopBar,
} from './styles';

interface IAnswer {
  text: string;
  value: number;
}

interface IQuestion {
  text: string;
  answers: IAnswer[];
}

interface IResearch {
  title: string;
  description: string;
  questions: IQuestion[];
}

const ANSWER_PALETTE = [
  PALETTE.green,
  PALETTE.orange,
  PALETTE.blue,
  PALETTE.greenAccent,
  PALETTE.orangeDark,
  PALETTE.warning,
  PALETTE.subtle,
  PALETTE.danger,
];

const formatNumber = (n: number) =>
  new Intl.NumberFormat('pt-BR').format(n || 0);

const percent = (part: number, total: number) => {
  if (!total) return 0;
  return Math.round((part / total) * 100);
};

const KpiSkeleton: React.FC = () => (
  <>
    <Skeleton $w="60%" $h={12} $mb={10} />
    <Skeleton $w="50%" $h={28} $mb={10} />
    <Skeleton $w="70%" $h={14} />
  </>
);

const DonutSkeleton: React.FC = () => (
  <DonutLayout>
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '8px 0',
      }}
    >
      <SkeletonCircle $size={150} />
    </div>
    <SkeletonStack>
      {[70, 55, 60, 45].map(w => (
        <SkeletonRowSpread key={w}>
          <Skeleton $w={`${w}%`} $h={12} />
          <Skeleton $w="18%" $h={12} />
        </SkeletonRowSpread>
      ))}
    </SkeletonStack>
  </DonutLayout>
);

const QuestionCardSkeleton: React.FC = () => (
  <Card>
    <CardHeader>
      <div className="titles" style={{ width: '80%' }}>
        <Skeleton $w="40%" $h={12} $mb={8} />
        <Skeleton $w="90%" $h={18} />
      </div>
    </CardHeader>
    <DonutSkeleton />
  </Card>
);

const ResearchResults: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();

  const [loading, setLoading] = useState(true);
  const [research, setResearch] = useState<IResearch>();

  useEffect(() => {
    document.title = 'Resultados da Pesquisa - Viva Voz';

    let cancelled = false;
    const load = async () => {
      try {
        const response = await api.get<IResearch>(`/research/${id}`, {
          params: { results: true },
        });
        if (!cancelled) setResearch(response.data);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const normalizedQuestions = useMemo(() => {
    if (!research?.questions) return [];
    return research.questions.map(q => {
      const answers = (q.answers || [])
        .map(a => ({
          text: (a.text || '').trim() || 'Sem resposta',
          value: a.value || 0,
          isBlank: !(a.text || '').trim(),
        }))
        .filter(a => a.value > 0 || !a.isBlank);

      const total = answers.reduce((acc, a) => acc + a.value, 0);
      const chartData = answers.map((a, idx) => ({
        name: a.text,
        value: a.value,
        fill: a.isBlank
          ? PALETTE.subtle
          : ANSWER_PALETTE[idx % ANSWER_PALETTE.length],
      }));

      const top = [...chartData].sort((a, b) => b.value - a.value)[0];

      return {
        text: (q.text || '').trim(),
        chartData,
        total,
        top,
      };
    });
  }, [research]);

  const totalQuestions = normalizedQuestions.length;
  const totalAnswers = useMemo(
    () => normalizedQuestions.reduce((acc, q) => acc + q.total, 0),
    [normalizedQuestions],
  );
  const maxRespondents = useMemo(
    () =>
      normalizedQuestions.reduce(
        (acc, q) => (q.total > acc ? q.total : acc),
        0,
      ),
    [normalizedQuestions],
  );

  return (
    <BasePage title="Resultados da Pesquisa">
      <Container fluid>
        <ResultsContainer>
          <BackLink type="button" onClick={() => history.push('/research')}>
            <FiArrowLeft size={14} />
            Voltar para pesquisas
          </BackLink>

          <TopBar>
            <div>
              <h2>
                {loading ? (
                  <Skeleton $w={320} $h={28} />
                ) : (
                  research?.title || 'Pesquisa'
                )}
              </h2>
              <div className="description">
                {loading ? (
                  <Skeleton $w="80%" $h={14} />
                ) : (
                  research?.description || 'Nenhuma descrição informada'
                )}
              </div>
            </div>
          </TopBar>

          <KpiRow>
            <KpiCell $tone="green">
              {loading ? (
                <KpiSkeleton />
              ) : (
                <>
                  <div className="label">
                    <span className="icon">
                      <FiHelpCircle size={14} />
                    </span>
                    Perguntas
                  </div>
                  <div className="value">{formatNumber(totalQuestions)}</div>
                  <div className="delta">
                    <FiClipboard size={12} />
                    {totalQuestions === 1
                      ? 'pergunta no formulário'
                      : 'perguntas no formulário'}
                  </div>
                </>
              )}
            </KpiCell>

            <KpiCell $tone="orange">
              {loading ? (
                <KpiSkeleton />
              ) : (
                <>
                  <div className="label">
                    <span className="icon">
                      <FiUsers size={14} />
                    </span>
                    Respostas coletadas
                  </div>
                  <div className="value">{formatNumber(totalAnswers)}</div>
                  <div className="delta">
                    {maxRespondents > 0
                      ? `até ${formatNumber(
                          maxRespondents,
                        )} respondentes por pergunta`
                      : 'nenhuma resposta recebida'}
                  </div>
                </>
              )}
            </KpiCell>

            <KpiCell $tone="blue">
              {loading ? (
                <KpiSkeleton />
              ) : (
                <>
                  <div className="label">
                    <span className="icon">
                      <FiAward size={14} />
                    </span>
                    Média por pergunta
                  </div>
                  <div className="value">
                    {formatNumber(
                      totalQuestions
                        ? Math.round(totalAnswers / totalQuestions)
                        : 0,
                    )}
                  </div>
                  <div className="delta">respostas em média</div>
                </>
              )}
            </KpiCell>
          </KpiRow>

          <QuestionGrid>
            {loading &&
              [0, 1].map(i => <QuestionCardSkeleton key={`skel-${i}`} />)}

            {!loading &&
              normalizedQuestions.map((q, index) => (
                <Card key={q.text}>
                  <CardHeader>
                    <div className="titles" style={{ display: 'flex' }}>
                      <QuestionNumber>
                        {String(index + 1).padStart(2, '0')}
                      </QuestionNumber>
                      <div style={{ minWidth: 0 }}>
                        <h3>{q.text}</h3>
                        <span className="subtitle">
                          {`${formatNumber(q.total)} ${
                            q.total === 1 ? 'resposta' : 'respostas'
                          }`}
                        </span>
                      </div>
                    </div>
                    {q.total > 0 && (
                      <InsightPill>
                        <FiUsers size={12} />
                        {`${formatNumber(q.total)}`}
                      </InsightPill>
                    )}
                  </CardHeader>

                  {q.chartData.length > 0 && q.total > 0 ? (
                    <FadeIn>
                      <DonutLayout>
                        <DonutCenter>
                          <ResponsiveContainer width="100%" height={200}>
                            <PieChart>
                              <Pie
                                data={q.chartData}
                                dataKey="value"
                                nameKey="name"
                                innerRadius={55}
                                outerRadius={85}
                                paddingAngle={2}
                                stroke="none"
                              >
                                {q.chartData.map(entry => (
                                  <Cell key={entry.name} fill={entry.fill} />
                                ))}
                              </Pie>
                              <Tooltip content={<ChartTooltip />} />
                            </PieChart>
                          </ResponsiveContainer>
                          <div className="center">
                            <span className="big">{formatNumber(q.total)}</span>
                            <span className="small">Respostas</span>
                          </div>
                        </DonutCenter>

                        <LegendList>
                          {q.chartData.map(e => (
                            <LegendItem key={e.name}>
                              <span
                                className="dot"
                                style={{ background: e.fill }}
                              />
                              <span className="name" title={e.name}>
                                {e.name}
                              </span>
                              <span className="value">
                                {formatNumber(e.value)}
                              </span>
                              <span className="percent">
                                {`${percent(e.value, q.total)}%`}
                              </span>
                            </LegendItem>
                          ))}
                        </LegendList>

                        {q.top && q.top.value > 0 && (
                          <HighlightRow>
                            <span className="label">Resposta predominante</span>
                            <div className="content">
                              <span
                                className="dot"
                                style={{ background: q.top.fill }}
                              />
                              <span>{q.top.name}</span>
                              <span className="pct">
                                {`${percent(q.top.value, q.total)}%`}
                              </span>
                            </div>
                          </HighlightRow>
                        )}
                      </DonutLayout>
                    </FadeIn>
                  ) : (
                    <EmptyState>Sem respostas registradas</EmptyState>
                  )}
                </Card>
              ))}

            {!loading && normalizedQuestions.length === 0 && (
              <Card style={{ gridColumn: '1 / -1' }}>
                <EmptyState>
                  Esta pesquisa ainda não possui perguntas cadastradas
                </EmptyState>
              </Card>
            )}
          </QuestionGrid>
        </ResultsContainer>
      </Container>
    </BasePage>
  );
};

export default ResearchResults;
